import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

declare global {
	interface Window {
		NeptingSDK: any;
		NeptingGooglePaySDK: any;
		NeptingApplePaySDK: any;
	}
}

const naptingPaymentUrl = environment.production
	? 'https://nepsa1.nepting.com/epayssp/payment'
	: 'https://qualif.nepting.com/epay/payment';

@Injectable({ providedIn: 'root' })
export class NeptingPaymentService {
	private sdkReady = new BehaviorSubject<boolean>(false);
	private sdkGoogleReady = new BehaviorSubject<boolean>(false);
	private sdkAppleReady = new BehaviorSubject<boolean>(false);

	sdkReady$ = this.sdkReady.asObservable();
	sdkGoogleReady$ = this.sdkGoogleReady.asObservable();
	sdkAppleReady$ = this.sdkAppleReady.asObservable();

	private hfields: any;
	public formValid$ = new BehaviorSubject<boolean>(false);
	public hostedToken$ = new BehaviorSubject<any | null>(null);

	constructor() {
		this.waitForSdk('NeptingSDK', this.sdkReady);
		this.waitForSdk('NeptingGooglePaySDK', this.sdkGoogleReady);
		this.waitForSdk('NeptingApplePaySDK', this.sdkAppleReady);
	}

	private waitForSdk(globalKey: keyof Window, subject: BehaviorSubject<boolean>) {
		let attempts = 0;
		const interval = setInterval(() => {
			attempts++;
			if (window[globalKey]) {
				subject.next(true);
				clearInterval(interval);
			}
			if (attempts > 50) clearInterval(interval);
		}, 100);
	}

	configureTransaction(brandId: string, merchantId: string, trx: string) {
		this.resetNeptingSdkState();

		const origin = window.location.hostname;
		window.NeptingSDK?.setTransactionInfo(brandId, merchantId, trx, origin);
		window.NeptingSDK?.setDisplayConecs(true);
	}

	loadHostedFields(fields: any, validCb: Function) {
		this.hfields = window.NeptingSDK?.hostedFields({ fields });

		this.hfields?.load(token => {
			this.hostedToken$.next(token);
		}, validCb);
	}

	collectCardData() {
		this.hfields?.collectCardData();
	}

	async redirectToNeptingRaw(payload: Record<string, any>) {
		const form = document.createElement('form');
		form.method = 'POST';
		form.action = naptingPaymentUrl;
		form.style.display = 'none';

		Object.entries(payload)
			.filter(([, v]) => v != null)
			.forEach(([key, value]) => {
				const input = document.createElement('input');
				input.type = 'hidden';
				input.name = key;
				input.value = String(value);
				form.appendChild(input);
			});

		document.body.appendChild(form);
		form.submit();
	}

	base64Encode(value: string) {
		try {
			return btoa(unescape(encodeURIComponent(value)));
		} catch {
			return '';
		}
	}

	private resetNeptingSdkState() {
		const sdk = window.NeptingSDK;
		if (!sdk) return;

		// reset internal flags
		sdk.errors = [];
		sdk.holderNameReceived = false;
		sdk.cardReceived = false;
		sdk.expirationDateReceived = false;
		sdk.cryptogramReceived = false;

		// remove hosted field iframes
		document.querySelectorAll('.nepting-epay-input-frame').forEach(el => el.remove());
	}

	destroyHostedFields() {
		this.hfields = null;
		this.resetNeptingSdkState();
	}
}
